import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  RefreshCw,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const providerOptions = [
  { value: "all", label: "Tüm Entegrasyonlar" },
  { value: "trendyol", label: "Trendyol" },
  { value: "hepsiburada", label: "Hepsiburada" },
  { value: "woocommerce", label: "WooCommerce" },
  { value: "amazon", label: "Amazon" },
  { value: "n11", label: "N11" },
  { value: "parasut", label: "Paraşüt" },
  { value: "bizimhesap", label: "BizimHesap" },
];

export default function IntegrationJobsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [search, setSearch] = useState("");
  const [provider, setProvider] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const perPage = 25;

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
      });
      if (search.trim()) params.set("search", search.trim());
      if (provider !== "all") params.set("provider", provider);
      if (status !== "all") params.set("status", status);
      const res = await api.get(`/integration-jobs?${params.toString()}`);
      setRows(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total || 0));
      setSelectedIds(new Set());
    } catch (error) {
      toast.error(error.message || "Entegrasyon işlemleri alınamadı");
    } finally {
      setLoading(false);
    }
  }, [page, search, provider, status]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const retryableIds = useMemo(
    () => rows.filter((r) => r?.retryable && r?.status === "failed").map((r) => String(r.id)),
    [rows]
  );
  const selectedRetryableIds = useMemo(
    () => [...selectedIds].filter((id) => retryableIds.includes(id)),
    [selectedIds, retryableIds]
  );

  const toggleRow = (id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    const ids = rows.map((r) => String(r.id));
    const all = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    if (all) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(ids));
  };

  const handleRetry = async () => {
    if (!selectedRetryableIds.length) {
      toast.error("Tekrar denenecek başarısız işlem seçin");
      return;
    }
    setRetrying(true);
    try {
      const res = await api.post("/integration-jobs/retry", { job_ids: selectedRetryableIds });
      toast.success(`Tekrar denendi: ${res?.success_count || 0} başarılı, ${res?.failed_count || 0} başarısız`);
      await fetchJobs();
    } catch (error) {
      toast.error(error.message || "Tekrar deneme başarısız");
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="page-shell space-y-6 min-h-[calc(100dvh-3.5rem)] overflow-x-hidden">
      <div className="page-header">
        <div className="app-page-header">
          <h1 className="app-page-title">Entegrasyon İşlemleri</h1>
          <p className="app-page-subtitle">{!loading && total > 0 ? `${total} kayıt` : "Senkronizasyon ve entegrasyon işlemlerini takip edin."}</p>
        </div>
        <div className="page-header-actions">
          <Button variant="outline" size="sm" onClick={fetchJobs} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Yenile
          </Button>
          <Button size="sm" onClick={handleRetry} disabled={retrying || selectedRetryableIds.length === 0}>
            {retrying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
            Tekrar Dene
          </Button>
        </div>
      </div>
      <div className="surface-card overflow-hidden">
        <div className="sheet-heading">
          <div>
            <h2>Entegrasyon İşlemleri</h2>
            {!loading && <p>{total} kayıt</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 border-b border-border bg-card px-3 py-3 md:grid-cols-[1fr_220px_180px] md:px-6">
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Ürün adı, SKU, barkod veya mesaj ara"
          />
          <Select
            value={provider}
            onValueChange={(val) => {
              setProvider(val);
              setPage(1);
            }}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {providerOptions.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select
            value={status}
            onValueChange={(val) => {
              setStatus(val);
              setPage(1);
            }}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              <SelectItem value="success">Başarılı</SelectItem>
              <SelectItem value="failed">Başarısız</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-auto bg-card/70">
          <table className="min-w-[980px] w-full text-sm">
            <thead className="sticky top-0 z-10 border-b border-border bg-muted/50">
              <tr>
                <th className="w-10 px-3 py-3 text-left"><Checkbox checked={rows.length > 0 && rows.every((r) => selectedIds.has(String(r.id)))} onCheckedChange={toggleAll} /></th>
                <th className="px-3 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Ürün</th>
                <th className="px-3 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Entegrasyon</th>
                <th className="px-3 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Durum</th>
                <th className="px-3 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Mesaj</th>
                <th className="px-3 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Son Kontrol</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">Yükleniyor...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">Kayıt bulunamadı</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2"><Checkbox checked={selectedIds.has(String(r.id))} onCheckedChange={() => toggleRow(r.id)} /></td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-foreground">{r.product_name || "-"}</div>
                    <div className="text-xs text-muted-foreground">{r.sku || "-"} {r.barcode ? `| ${r.barcode}` : ""}</div>
                  </td>
                  <td className="px-3 py-2 text-foreground/80">{r.integration_no ? `#${r.integration_no} ` : ""}{r.integration_name || r.provider_label}</td>
                  <td className="px-3 py-2">
                    <span className={r.status === "failed" ? "rounded-md bg-rose-100 px-2 py-1 text-xs font-medium text-rose-700" : "rounded-md bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700"}>
                      {r.status === "failed" ? "Başarısız" : "Başarılı"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-foreground/80">{r.message || "-"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{String(r.timestamp || "").replace("T", " ").slice(0, 16)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border bg-card px-3 py-3 text-sm md:px-6">
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span>{page} / {totalPages}</span>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
